import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  Box,
  Grid,
  IconButton,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from '@mui/material';

import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ImageIcon from '@mui/icons-material/Image';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { apiRequest } from '../utils/api';
import { buildOCCSymbol, roundSPXTo5Cents } from '../utils/optionSymbol';
import { parseTradeAlert } from '../utils/discordParser';
import { fetchNearestWeeklyExpiration } from '../utils/optionExpirations';
import { usePaperMode } from '../contexts/PaperModeContext';
import PayoffDiagram from './PayoffDiagram';
import UnderlyingPriceChart from './UnderlyingPriceChart';

function parseExpiration(exp) {
  if (!exp) return null;
  const upper = String(exp).toUpperCase();
  if (upper === '0DTE') return new Date().toISOString().split('T')[0];
  if (/^\d+DTE$/.test(upper)) {
    const days = parseInt(upper);
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(upper)) return upper;
  const m = upper.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (m) {
    const month = parseInt(m[1]);
    const day = parseInt(m[2]);
    let year = m[3] ? parseInt(m[3]) : null;
    if (!year) {
      const now = new Date();
      const thisYear = now.getFullYear();
      // Compare date-only to avoid today's date being pushed to next year
      const todayMidnight = new Date(thisYear, now.getMonth(), now.getDate());
      const testDate = new Date(thisYear, month - 1, day);
      year = testDate >= todayMidnight ? thisYear : thisYear + 1;
    } else if (year < 100) {
      year += 2000;
    }
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

/**
 * Normalize an instruction string to a known option or equity instruction.
 * Handles short forms like BTC, STO, STC, BTO and generic words like BUY/SELL.
 */
function normalizeInstruction(instruction, assetType = 'OPTION') {
  if (!instruction) return assetType === 'EQUITY' ? 'BUY' : 'BUY_TO_OPEN';
  const upper = String(instruction).toUpperCase().replace(/\s+/g, '_').replace(/-+/g, '_');

  if (assetType === 'EQUITY') {
    if (['BUY', 'BOUGHT', 'BTO', 'OPENED'].includes(upper)) return 'BUY';
    if (['SELL', 'SOLD', 'STC', 'CLOSED', 'ALL_OUT'].includes(upper)) return 'SELL';
    if (['BUY_TO_COVER', 'BTC', 'COVERED', 'COVER'].includes(upper)) return 'BUY_TO_COVER';
    if (['SELL_SHORT', 'STO', 'SHORT'].includes(upper)) return 'SELL_SHORT';
    return 'BUY';
  }

  // Options
  if (['BUY_TO_OPEN', 'BTO', 'BOUGHT', 'OPENED', 'BUY'].includes(upper)) return 'BUY_TO_OPEN';
  if (['SELL_TO_OPEN', 'STO', 'SHORT', 'SELL'].includes(upper)) return 'SELL_TO_OPEN';
  if (['BUY_TO_CLOSE', 'BTC', 'COVERED', 'COVER'].includes(upper)) return 'BUY_TO_CLOSE';
  if (['SELL_TO_CLOSE', 'STC', 'SOLD', 'CLOSED', 'ALL_OUT'].includes(upper)) return 'SELL_TO_CLOSE';
  return 'BUY_TO_OPEN';
}

function alertToOrderLegs(alert) {
  if (!alert) return [];

  // Equity: single leg with symbol, instruction, and quantity
  if (alert.assetType === 'EQUITY' || alert.strategyType === 'equity') {
    return [{
      assetType: 'EQUITY',
      symbol: (alert.symbol || alert.underlying || '').toUpperCase(),
      instruction: normalizeInstruction(alert.instruction || alert.action, 'EQUITY'),
      quantity: parseInt(alert.quantity) || 1,
    }];
  }

  // Price alert close: use closeData legs directly (already has instruction, putCall, strike, expiration)
  if (alert.closeData && alert.closeData.legs) {
    return alert.closeData.legs.map(leg => {
      if (leg.assetType === 'EQUITY') {
        return {
          assetType: 'EQUITY',
          symbol: leg.symbol,
          instruction: normalizeInstruction(leg.instruction, 'EQUITY'),
          quantity: leg.quantity || 1,
        };
      }
      return {
        instruction: normalizeInstruction(leg.instruction, 'OPTION'),
        putCall: leg.putCall,
        strike: leg.strike,
        expiration: leg.expiration,
        quantity: leg.quantity || 1,
      };
    });
  }

  // Custom strategy: legs already have instruction, putCall, strike, expiration, quantity
  if (alert.strategyType === 'custom' && alert.legs) {
    return alert.legs.map(leg => {
      if (leg.assetType === 'EQUITY') {
        return {
          assetType: 'EQUITY',
          symbol: leg.symbol,
          instruction: normalizeInstruction(leg.instruction, 'EQUITY'),
          quantity: leg.quantity || 1,
        };
      }
      return {
        instruction: normalizeInstruction(leg.instruction, 'OPTION'),
        putCall: leg.side || leg.putCall,
        strike: leg.strike,
        expiration: leg.expiration,
        quantity: leg.quantity || 1,
      };
    });
  }

  if (!alert.symbol) return [];
  const expiration = parseExpiration(alert.expiration);
  if (!expiration) return [];

  // Build legs array if not present (e.g. slash-separated strikes without side letters)
  let alertLegs = alert.legs || [];
  if (alertLegs.length === 0 && alert.strikes && alert.strikes.length > 0) {
    // Infer side from strategy name
    const isCall = /call|ccs|cds/i.test(alert.strategy || '');
    const isPut = /put|pcs|pds/i.test(alert.strategy || '');
    const side = isCall ? 'C' : isPut ? 'P' : 'C';
    alertLegs = alert.strikes.map(s => ({ strike: s, side }));
  }
  if (alertLegs.length === 0) return [];

  const legs = [];
  const isCredit = alert.strategyType === 'credit';
  const normalizedAction = normalizeInstruction(alert.action || alert.instruction, 'OPTION');
  const isExit = normalizedAction === 'BUY_TO_CLOSE' || normalizedAction === 'SELL_TO_CLOSE';

  if (alertLegs.length === 1) {
    const leg = alertLegs[0];
    legs.push({ instruction: normalizedAction, putCall: leg.side, strike: leg.strike, expiration });
  } else if (alertLegs.length === 2) {
    if (alert.strategy === 'Strangle' || alert.strategy === 'Straddle') {
      const instr = isExit ? 'BUY_TO_CLOSE' : 'SELL_TO_OPEN';
      alertLegs.forEach(leg => {
        legs.push({ instruction: instr, putCall: leg.side, strike: leg.strike, expiration });
      });
    } else {
      const shortLeg = alertLegs.find(l => l.strike === alert.shortStrike) || alertLegs[0];
      const longLeg = alertLegs.find(l => l.strike === alert.longStrike) || alertLegs[1];
      if (isCredit) {
        legs.push({ instruction: 'SELL_TO_OPEN', putCall: shortLeg.side, strike: shortLeg.strike, expiration });
        legs.push({ instruction: 'BUY_TO_OPEN', putCall: longLeg.side, strike: longLeg.strike, expiration });
      } else {
        legs.push({ instruction: 'BUY_TO_OPEN', putCall: shortLeg.side, strike: shortLeg.strike, expiration });
        legs.push({ instruction: 'SELL_TO_OPEN', putCall: longLeg.side, strike: longLeg.strike, expiration });
      }
    }
  } else if (alertLegs.length === 4) {
    const puts = alertLegs.filter(l => l.side === 'P').sort((a, b) => a.strike - b.strike);
    const calls = alertLegs.filter(l => l.side === 'C').sort((a, b) => a.strike - b.strike);
    if (puts.length === 2 && calls.length === 2) {
      legs.push({ instruction: 'BUY_TO_OPEN', putCall: 'P', strike: puts[0].strike, expiration });
      legs.push({ instruction: 'SELL_TO_OPEN', putCall: 'P', strike: puts[1].strike, expiration });
      legs.push({ instruction: 'SELL_TO_OPEN', putCall: 'C', strike: calls[0].strike, expiration });
      legs.push({ instruction: 'BUY_TO_OPEN', putCall: 'C', strike: calls[1].strike, expiration });
    }
  }

  return legs;
}

function calculateNetMid(builtLegs, quotes) {
  if (!builtLegs || !quotes) return null;
  let netMid = 0;
  let allFound = true;
  for (const leg of builtLegs) {
    const symKey = leg.symbol.replace(/\s+/g, '');
    const q = quotes[symKey];
    if (!q || (!q.bid && !q.ask)) { allFound = false; continue; }
    const isBuy = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'BUY_TO_CLOSE' || leg.instruction === 'BUY' || leg.instruction === 'BUY_TO_COVER';
    const roundedMid = roundSPXTo5Cents(q.mid, isBuy, q.underlyingSymbol || leg.symbol);
    netMid += isBuy ? roundedMid : -roundedMid;
  }
  if (!allFound) return null;
  return netMid;
}

function buildLegSymbols(underlying, legs) {
  return legs.map(l => {
    if (l.assetType === 'EQUITY' || l.symbol) {
      return { ...l, symbol: (l.symbol || underlying).toUpperCase() };
    }
    return {
      ...l,
      symbol: buildOCCSymbol(underlying, l.expiration, l.putCall, parseFloat(l.strike)),
    };
  });
}

const INSTRUCTION_LABELS = {
  BUY_TO_OPEN: 'Buy to Open',
  SELL_TO_OPEN: 'Sell to Open',
  BUY_TO_CLOSE: 'Buy to Close',
  SELL_TO_CLOSE: 'Sell to Close',
  BUY: 'Buy',
  SELL: 'Sell',
  BUY_TO_COVER: 'Buy to Cover',
  SELL_SHORT: 'Sell Short',
};

const OPEN_TO_CLOSE = {
  BUY_TO_OPEN: 'SELL_TO_CLOSE',
  SELL_TO_OPEN: 'BUY_TO_CLOSE',
  BUY: 'SELL',
  SELL: 'BUY',
  BUY_TO_COVER: 'SELL_SHORT',
  SELL_SHORT: 'BUY_TO_COVER',
};

const STRATEGIES = [
  { value: 'single_call', label: 'Single Call', legs: 1, sides: ['C'] },
  { value: 'single_put', label: 'Single Put', legs: 1, sides: ['P'] },
  { value: 'ccs', label: 'Call Credit Spread', legs: 2, sides: ['C', 'C'], strategyType: 'credit' },
  { value: 'pcs', label: 'Put Credit Spread', legs: 2, sides: ['P', 'P'], strategyType: 'credit' },
  { value: 'cds', label: 'Call Debit Spread', legs: 2, sides: ['C', 'C'], strategyType: 'debit' },
  { value: 'pds', label: 'Put Debit Spread', legs: 2, sides: ['P', 'P'], strategyType: 'debit' },
  { value: 'strangle', label: 'Strangle', legs: 2, sides: ['P', 'C'], strategyType: 'credit' },
  { value: 'straddle', label: 'Straddle', legs: 2, sides: ['P', 'C'], strategyType: 'credit' },
  { value: 'iron_condor', label: 'Iron Condor', legs: 4, sides: ['P', 'P', 'C', 'C'], strategyType: 'credit' },
  { value: 'equity', label: 'Equity Shares', legs: 1, sides: [], strategyType: 'equity' },
  { value: 'custom', label: 'Custom (Build Your Own)', legs: 0, sides: [], strategyType: 'custom' },
];

const STRATEGY_NAME_MAP = {
  single_call: 'Call',
  single_put: 'Put',
  ccs: 'CCS',
  pcs: 'PCS',
  cds: 'CDS',
  pds: 'PDS',
  strangle: 'Strangle',
  straddle: 'Straddle',
  iron_condor: 'Iron Condor',
  equity: 'Equity',
};

const STRIKE_LABELS = {
  single_call: ['Strike'],
  single_put: ['Strike'],
  ccs: ['Short Strike', 'Long Strike'],
  pcs: ['Short Strike', 'Long Strike'],
  cds: ['Long Strike', 'Short Strike'],
  pds: ['Long Strike', 'Short Strike'],
  strangle: ['Put Strike', 'Call Strike'],
  straddle: ['Strike', 'Strike'],
  iron_condor: ['Long Put', 'Short Put', 'Short Call', 'Long Call'],
};

const EXAMPLES = [
  'STO | SPX | 7535C & 7545C | 7/23 exp | 1.15 cr',
  'BTO | SPY | 5800C | 0DTE exp | 2.50 db',
  'BOUGHT 0DTE SPX 6610p @ 2.0',
  'ENTRY CCS SPX 7550/7560 1DTE @ 1.15',
];

export default function TradeDialog({ open, alert, onClose, showInputSection = false, inline = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { paperMode } = usePaperMode();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [quoteAccountId, setQuoteAccountId] = useState(null);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [underlying, setUnderlying] = useState('');
  const [orderLegs, setOrderLegs] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [orderType, setOrderType] = useState('LIMIT');
  const [duration, setDuration] = useState('DAY');
  const [price, setPrice] = useState('');
  const [paperTakeProfitPct, setPaperTakeProfitPct] = useState('');
  const [paperStopLossPct, setPaperStopLossPct] = useState('');
  const [autoPrice, setAutoPrice] = useState(true);
  const [stopPrice, setStopPrice] = useState('');
  const [stopLimitPrice, setStopLimitPrice] = useState('');
  const [trailingStopType, setTrailingStopType] = useState('MARKET'); // MARKET or LIMIT
  const [strategy, setStrategy] = useState('custom');
  const [trailingStopAmount, setTrailingStopAmount] = useState('');
  const [trailingStopPercent, setTrailingStopPercent] = useState(false);
  const [legQuotes, setLegQuotes] = useState({});
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const quoteIntervalRef = useRef(null);
  const [otoEnabled, setOtoEnabled] = useState(false);
  const [otoEntryPrice, setOtoEntryPrice] = useState('');
  
  // OTO child order stop parameters
  const [otoChildOrderType, setOtoChildOrderType] = useState('LIMIT');
  const [otoStopPrice, setOtoStopPrice] = useState('');
  const [otoStopLimitPrice, setOtoStopLimitPrice] = useState('');
  const [otoTrailingStopType, setOtoTrailingStopType] = useState('MARKET');
  const [otoTrailingStopAmount, setOtoTrailingStopAmount] = useState('');
  const [otoTrailingStopPercent, setOtoTrailingStopPercent] = useState(false);
  
  // OCO stop order (separate from limit exits)
  const [ocoStopEnabled, setOcoStopEnabled] = useState(false);
  const [ocoStopOrderType, setOcoStopOrderType] = useState('STOP');
  const [ocoStopPrice, setOcoStopPrice] = useState('');
  const [ocoStopLimitPrice, setOcoStopLimitPrice] = useState('');
  const [ocoTrailingStopType, setOcoTrailingStopType] = useState('MARKET');
  const [ocoTrailingStopAmount, setOcoTrailingStopAmount] = useState('');
  const [ocoTrailingStopPercent, setOcoTrailingStopPercent] = useState(false);
  
  // Chains data for Greeks (delta, IV, open interest)
  const [chainsData, setChainsData] = useState({});
  const [chainsLoading, setChainsLoading] = useState(false);
  const [underlyingQuote, setUnderlyingQuote] = useState(null);
  
  // Exit orders: up to 4, each with { pct, qty, price, enabled }
  const [exitOrders, setExitOrders] = useState([
    { pct: 30, qty: '', price: '', enabled: true },
    { pct: 50, qty: '', price: '', enabled: true },
    { pct: 100, qty: '', price: '', enabled: true },
    { pct: 200, qty: '', price: '', enabled: true },
  ]);

  // Input section state (for showInputSection mode)
  const [inputMode, setInputMode] = useState('paste');
  const [rawText, setRawText] = useState('');
  const [inputError, setInputError] = useState(null);
  const [parsingImage, setParsingImage] = useState(false);
  const [parsingAI, setParsingAI] = useState(false);
  const [manualStrategy, setManualStrategy] = useState('single_call');
  const [manualSymbol, setManualSymbol] = useState('');
  const [manualExpiration, setManualExpiration] = useState('');
  const [manualAction, setManualAction] = useState('buy_to_open');
  const [manualStrikes, setManualStrikes] = useState(['', '', '', '']);
  const [manualPrice, setManualPrice] = useState('');
  // Custom strategy legs state
  const [customLegs, setCustomLegs] = useState([]);

  // Saved trades state
  const [savedTrades, setSavedTrades] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedTradesLoading, setSavedTradesLoading] = useState(false);
  const manualStrategyConfig = useMemo(
    () => STRATEGIES.find(s => s.value === manualStrategy) || STRATEGIES[0],
    [manualStrategy]
  );

  // Extracted function to apply an alert to order state
  const applyAlert = useCallback((alertData) => {
    if (!alertData) return;
    setError(null);
    setResult(null);
    setUnderlying(alertData.symbol || '');
    const legs = alertToOrderLegs(alertData);
    setOrderLegs(legs);
    setPrice(alertData.price ? String(alertData.price) : '');
    setAutoPrice(!alertData.price);
    setQuantity(parseInt(alertData.quantity) || 1);
    setOrderType(alertData.orderType || 'LIMIT');
    setDuration(alertData.duration || 'DAY');
    setOtoEnabled(!!alertData.otoEnabled);
    if (alertData.assetType === 'EQUITY' || alertData.strategyType === 'equity') {
      setStrategy('equity');
    } else if (alertData.strategyType === 'custom' || (alertData.legs && alertData.legs.length > 0)) {
      setStrategy('custom');
    } else if (alertData.strategy && STRATEGIES.some(s => s.value === alertData.strategy.toLowerCase())) {
      setStrategy(alertData.strategy.toLowerCase());
    }
    const entryStr = alertData.otoEntryPrice ? String(alertData.otoEntryPrice) : (alertData.price ? String(alertData.price) : '');
    setOtoEntryPrice(entryStr);
    const entry = parseFloat(entryStr);
    const isCredit = alertData.strategyType === 'credit';
    const totalQty = parseInt(alertData.quantity) || 1;
    const base = Math.floor(totalQty / 4);
    const remainder = totalQty % 4;
    const defaultPcts = [30, 50, 100, 200];
    setExitOrders(defaultPcts.map((pct, i) => {
      const exitQty = base + (i < remainder ? 1 : 0);
      let exitPrice = '';
      if (Number.isFinite(entry) && entry > 0) {
        const ep = isCredit ? entry * (1 - pct / 100) : entry * (1 + pct / 100);
        exitPrice = String(Math.max(0.01, Math.round(ep * 100) / 100));
      }
      return { pct, qty: String(exitQty), price: exitPrice, enabled: exitQty > 0 };
    }));
  }, []);

  // Reset form to defaults
  const resetForm = useCallback(() => {
    setUnderlying('');
    setOrderLegs([]);
    setQuantity(1);
    setOrderType('LIMIT');
    setDuration('DAY');
    setPrice('');
    setPaperTakeProfitPct('');
    setPaperStopLossPct('');
    setAutoPrice(true);
    setStopPrice('');
    setStopLimitPrice('');
    setStrategy('custom');
    setLegQuotes({});
    setResult(null);
    setError(null);
    setRawText('');
    setInputError(null);
    setManualSymbol('');
    setManualExpiration('');
    setManualStrikes(['', '', '', '']);
    setManualPrice('');
    setCustomLegs([]);
    setOtoEnabled(false);
    setOtoEntryPrice('');
    setOcoStopEnabled(false);
    setOcoStopPrice('');
    setExitOrders([
      { pct: 30, qty: '', price: '', enabled: true },
      { pct: 50, qty: '', price: '', enabled: true },
      { pct: 100, qty: '', price: '', enabled: true },
      { pct: 200, qty: '', price: '', enabled: true },
    ]);
    lastAppliedAlert.current = null;
  }, []);

  // Fetch saved trades from server
  const fetchSavedTrades = useCallback(async () => {
    setSavedTradesLoading(true);
    try {
      const resp = await apiRequest('/api/saved-trades');
      setSavedTrades(resp.data?.trades || []);
    } catch (err) {
      console.warn('[TradeDialog] Failed to fetch saved trades:', err);
    } finally {
      setSavedTradesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSavedTrades();
    }
  }, [open, fetchSavedTrades]);

  // Save current form state
  const generateSaveName = () => {
    const parts = [];
    if (underlying) parts.push(underlying.toUpperCase());
    const stratName = STRATEGY_NAME_MAP[strategy] || strategy;
    if (stratName) parts.push(stratName);

    // Collect strikes from order legs
    const strikes = orderLegs
      .map(l => l.strike)
      .filter(s => s !== undefined && s !== '' && s !== null)
      .map(s => String(s));
    if (strikes.length > 0) parts.push(strikes.join('/'));

    // Use earliest expiration from legs
    const expirations = orderLegs
      .map(l => l.expiration)
      .filter(e => e)
      .sort();
    if (expirations.length > 0) {
      const exp = expirations[0];
      const m = exp.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        parts.push(`${m[2]}/${m[3]}`);
      } else {
        parts.push(exp);
      }
    }

    return parts.length > 0 ? parts.join('_') : 'Untitled_Trade';
  };

  const handleSaveTrade = async () => {
    if (!saveName.trim()) return;
    const tradeData = {
      selectedAccountId,
      underlying,
      orderLegs,
      quantity,
      orderType,
      duration,
      price,
      autoPrice,
      stopPrice,
      stopLimitPrice,
      strategy,
      otoEnabled,
      otoEntryPrice,
      otoChildOrderType,
      otoStopPrice,
      otoStopLimitPrice,
      ocoStopEnabled,
      ocoStopOrderType,
      ocoStopPrice,
      ocoStopLimitPrice,
      exitOrders,
      trailingStopType,
      trailingStopAmount,
      trailingStopPercent,
    };
    try {
      await apiRequest('/api/saved-trades', {
        method: 'POST',
        body: JSON.stringify({ name: saveName.trim(), tradeData }),
      });
      setSaveName('');
      setSaveDialogOpen(false);
      fetchSavedTrades();
    } catch (err) {
      setError('Failed to save trade: ' + (err.message || 'Unknown error'));
    }
  };

  // Load a saved trade
  const handleLoadTrade = async (tradeId) => {
    const trade = savedTrades.find(t => t.id === tradeId);
    if (!trade || !trade.tradeData) return;
    const d = trade.tradeData;
    setSelectedAccountId(d.selectedAccountId || '');
    setUnderlying(d.underlying || '');
    setOrderLegs(d.orderLegs || []);
    setQuantity(d.quantity || 1);
    setOrderType(d.orderType || 'LIMIT');
    setDuration(d.duration || 'DAY');
    setPrice(d.price || '');
    setAutoPrice(d.autoPrice ?? true);
    setStopPrice(d.stopPrice || '');
    setStopLimitPrice(d.stopLimitPrice || '');
    setStrategy(d.strategy || 'custom');
    setOtoEnabled(d.otoEnabled || false);
    setOtoEntryPrice(d.otoEntryPrice || '');
    setOcoStopEnabled(d.ocoStopEnabled || false);
    setOcoStopOrderType(d.ocoStopOrderType || 'STOP');
    setOcoStopPrice(d.ocoStopPrice || '');
    setOcoStopLimitPrice(d.ocoStopLimitPrice || '');
    setExitOrders(d.exitOrders || [
      { pct: 30, qty: '', price: '', enabled: true },
      { pct: 50, qty: '', price: '', enabled: true },
      { pct: 100, qty: '', price: '', enabled: true },
      { pct: 200, qty: '', price: '', enabled: true },
    ]);
    setResult(null);
    setError(null);
    lastAppliedAlert.current = null;
  };

  // Delete a saved trade
  const handleDeleteSavedTrade = async (tradeId) => {
    try {
      await apiRequest(`/api/saved-trades/${tradeId}`, { method: 'DELETE' });
      fetchSavedTrades();
    } catch (err) {
      console.warn('[TradeDialog] Failed to delete saved trade:', err);
    }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      setAccountsLoading(true);
      try {
        // Fetch Schwab accounts first for quote market data (always use Schwab for quotes)
        let schwabAccs = [];
        try {
          const schwabResp = await apiRequest('/api/schwab-api/accounts');
          schwabAccs = (schwabResp.data?.accounts || []).map(a => ({ ...a, accountType: 'schwab' }));
        } catch (schwabErr) {
          // Schwab may not be configured
        }
        const firstSchwab = schwabAccs[0];
        setQuoteAccountId(firstSchwab?.id || null);

        if (paperMode) {
          setAccounts([{ id: 'paper', name: 'Paper Account', accountType: 'paper' }]);
          setSelectedAccountId('paper');
          setAccountsLoading(false);
          return;
        }

        // Fetch Robinhood MCP accounts
        let robinhoodAccs = [];
        try {
          const rhResp = await apiRequest('/api/mcp/robinhood/accounts');
          robinhoodAccs = (rhResp.data?.accounts || []).map(a => ({ ...a, accountType: 'robinhood-mcp' }));
        } catch (rhErr) {
          // Robinhood MCP may not be configured — silently skip
        }

        // Fetch Robinhood direct-API accounts (stored OAuth token)
        let robinhoodDirectAccs = [];
        try {
          const allAccsResp = await apiRequest('/api/accounts');
          const rhDirect = (allAccsResp.data || allAccsResp || []).filter(
            a => ['robinhood', 'robinhood-api'].includes(String(a.apiType || a.api_type || '').toLowerCase())
          );
          robinhoodDirectAccs = rhDirect.map(a => ({
            id: String(a.id),
            name: a.name,
            accountType: 'robinhood',
            isDefault: Boolean(a.isDefault || a.is_default),
          }));
        } catch (e) {
          // No Robinhood direct-API accounts — silently skip
        }

        const allAccs = [...schwabAccs, ...robinhoodAccs, ...robinhoodDirectAccs];
        setAccounts(allAccs);
        if (allAccs.length > 0) {
          const def = allAccs.find(a => a.isDefault);
          setSelectedAccountId(def ? def.id : allAccs[0].id);
        }
      } catch (err) {
        setError('Failed to load accounts: ' + (err.message || 'Unknown error'));
      } finally {
        setAccountsLoading(false);
      }
    })();
  }, [open, paperMode]);

  const lastAppliedAlert = useRef(null);
  useEffect(() => {
    if (!open || !alert) return;
    if (lastAppliedAlert.current === alert) return;
    lastAppliedAlert.current = alert;
    applyAlert(alert);
  }, [open, alert, applyAlert]);

  // Pre-select the account from the alert payload once accounts are loaded
  useEffect(() => {
    if (!open || accountsLoading || accounts.length === 0 || !alert?.tradeAccountId) return;
    const match = accounts.find(a => String(a.id) === String(alert.tradeAccountId));
    if (match) {
      setSelectedAccountId(match.id);
    }
  }, [open, accountsLoading, accounts, alert?.tradeAccountId]);

  // Input section handlers
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setRawText(text);
        setInputError(null);
      }
    } catch (err) {
      // Clipboard API may not be available
    }
  };

  // Screenshot paste: broker order-entry screenshot -> server-side vision
  // parse -> applyAlert. Triggered by Ctrl+V on the paste box or the button.
  const handleScreenshotBlob = async (blob) => {
    if (!blob || parsingImage) return;
    setParsingImage(true);
    setInputError(null);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const resp = await apiRequest('/api/screenshot/parse', {
        method: 'POST',
        body: JSON.stringify({ image: dataUrl }),
      });
      if (!resp.data?.success || !resp.data?.alert) {
        setInputError(resp.data?.error || 'Could not extract a trade from the screenshot.');
        return;
      }
      applyAlert(resp.data.alert);
      setRawText('');
    } catch (err) {
      setInputError(err.response?.data?.error || `Screenshot parse failed: ${err.message}`);
    } finally {
      setParsingImage(false);
    }
  };

  const handleAlertPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        e.preventDefault();
        handleScreenshotBlob(item.getAsFile());
        return;
      }
    }
  };

  const handlePasteScreenshotClick = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          handleScreenshotBlob(await item.getType(imageType));
          return;
        }
      }
      setInputError('No image found on the clipboard. Copy a screenshot first.');
    } catch (err) {
      setInputError('Clipboard image access denied — click the paste box and press Ctrl+V instead.');
    }
  };

  const handleParseAlert = async () => {
    setInputError(null);
    if (!rawText.trim()) {
      setInputError('Paste a trade alert first.');
      return;
    }
    const parsed = parseTradeAlert(rawText);
    if (parsed && parsed.isTradeAlert && parsed.symbol && parsed.strikes.length > 0) {
      applyAlert(parsed);
      setInputError(null);
      return;
    }

    // Regex parser couldn't handle it — fall back to the AI parser.
    if (parsingAI) return;
    setParsingAI(true);
    try {
      const resp = await apiRequest('/api/screenshot/parse-text', {
        method: 'POST',
        body: JSON.stringify({ text: rawText }),
      });
      if (!resp.data?.success || !resp.data?.alert) {
        setInputError(resp.data?.error || 'Could not parse a trade alert from this text.');
        return;
      }
      applyAlert(resp.data.alert);
      setRawText('');
    } catch (err) {
      setInputError(err.response?.data?.error || `Could not parse a trade alert from this text.`);
    } finally {
      setParsingAI(false);
    }
  };

  const handleManualBuild = () => {
    setInputError(null);
    
    // Handle custom strategy
    if (manualStrategy === 'custom') {
      if (!manualSymbol.trim()) {
        setInputError('Underlying symbol is required.');
        return;
      }
      if (customLegs.length === 0) {
        setInputError('At least one leg is required for custom strategy.');
        return;
      }

      const builtLegs = [];
      // Validate custom legs
      for (let i = 0; i < customLegs.length; i++) {
        const leg = customLegs[i];
        if (!leg.instruction) {
          setInputError(`Instruction is required for leg ${i + 1}.`);
          return;
        }
        if (leg.assetType === 'EQUITY') {
          if (!leg.symbol || !leg.symbol.trim()) {
            setInputError(`Symbol is required for equity leg ${i + 1}.`);
            return;
          }
          builtLegs.push({
            assetType: 'EQUITY',
            symbol: leg.symbol.trim().toUpperCase(),
            instruction: leg.instruction,
            quantity: leg.quantity || 1
          });
        } else {
          if (!leg.strike || isNaN(parseFloat(leg.strike))) {
            setInputError(`Strike is required for leg ${i + 1}.`);
            return;
          }
          if (!leg.expiration) {
            setInputError(`Expiration is required for leg ${i + 1}.`);
            return;
          }
          if (!leg.putCall) {
            setInputError(`Call/Put is required for leg ${i + 1}.`);
            return;
          }
          builtLegs.push({
            strike: parseFloat(leg.strike),
            side: leg.putCall,
            expiration: leg.expiration,
            instruction: leg.instruction,
            quantity: leg.quantity || 1
          });
        }
      }
      
      const builtAlert = {
        isTradeAlert: true,
        alertType: 'entry',
        symbol: manualSymbol.toUpperCase().trim(),
        strategy: 'Custom',
        strategyType: 'custom',
        action: manualAction,
        legs: builtLegs,
        strikes: customLegs.filter(l => l.assetType !== 'EQUITY').map(leg => parseFloat(leg.strike)),
        price: manualPrice ? parseFloat(manualPrice) : null,
        priceUnit: null,
      };
      applyAlert(builtAlert);
      setInputError(null);
      return;
    }
    
    // Handle predefined strategies
    if (!manualSymbol.trim()) {
      setInputError('Underlying symbol is required.');
      return;
    }
    if (!manualExpiration) {
      setInputError('Expiration date is required.');
      return;
    }
    const numLegs = manualStrategyConfig.legs;
    const strikes = [];
    for (let i = 0; i < numLegs; i++) {
      const val = parseFloat(manualStrikes[i]);
      if (!val || isNaN(val)) {
        setInputError(`Strike ${i + 1} is required.`);
        return;
      }
      strikes.push(val);
    }
    const legs = manualStrategyConfig.sides.map((side, i) => ({ strike: strikes[i], side }));
    const builtAlert = {
      isTradeAlert: true,
      alertType: 'entry',
      symbol: manualSymbol.toUpperCase().trim(),
      strategy: STRATEGY_NAME_MAP[manualStrategy],
      strategyType: manualStrategyConfig.strategyType || null,
      action: manualAction,
      expiration: manualExpiration,
      strikes,
      legs,
      price: manualPrice ? parseFloat(manualPrice) : null,
      priceUnit: manualStrategyConfig.strategyType === 'credit' ? 'credit' : manualStrategyConfig.strategyType === 'debit' ? 'debit' : null,
      shortStrike: numLegs >= 2 ? strikes[0] : strikes[0],
      longStrike: numLegs >= 2 ? strikes[1] : null,
    };
    applyAlert(builtAlert);
    setInputError(null);
  };

  const handleManualStrikeChange = (idx, value) => {
    setManualStrikes(prev => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  // Custom strategy leg management
  const addCustomLeg = (type = 'OPTION') => {
    setCustomLegs(prev => [...prev, type === 'EQUITY' ? {
      assetType: 'EQUITY',
      symbol: manualSymbol.toUpperCase().trim() || '',
      instruction: 'BUY',
      quantity: 1
    } : {
      strike: '',
      expiration: manualExpiration || '',
      putCall: 'C',
      instruction: 'BUY_TO_OPEN',
      quantity: 1
    }]);
  };

  const removeCustomLeg = (idx) => {
    setCustomLegs(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCustomLeg = (idx, field, value) => {
    setCustomLegs(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  // Build legs with symbols for quote fetching and order placement
  const builtLegsWithSymbols = useCallback(() => {
    if (!underlying || orderLegs.length === 0) return [];
    return buildLegSymbols(underlying, orderLegs);
  }, [underlying, orderLegs]);

  const fetchQuotes = useCallback(async () => {
    if (!quoteAccountId || orderLegs.length === 0) return;
    const built = builtLegsWithSymbols();
    if (built.length === 0) return;
    const symbols = built.map(l => l.symbol);
    setQuotesLoading(true);
    try {
      const resp = await apiRequest('/api/schwab-api/quotes', {
        method: 'POST',
        body: JSON.stringify({ accountId: quoteAccountId, symbols }),
      });
      const quotes = resp.data?.quotes || {};
      // Skip no-op updates — a new object each poll would re-render the
      // payoff diagram and leg quote labels even when prices didn't move.
      setLegQuotes(prev => {
        const keys = Object.keys(quotes);
        const prevKeys = Object.keys(prev);
        if (keys.length === prevKeys.length && keys.every(k => {
          const a = prev[k], b = quotes[k];
          return a && b && a.bid === b.bid && a.ask === b.ask && a.mid === b.mid;
        })) return prev;
        return quotes;
      });
      if (autoPrice) {
        const netMid = calculateNetMid(built, quotes);
        if (netMid !== null) {
          setPrice(String(Math.round(Math.abs(netMid) * 100) / 100));
        }
      }
    } catch (err) {
      console.warn('[TradeDialog] Quote fetch error:', err);
    } finally {
      setQuotesLoading(false);
    }
  }, [quoteAccountId, orderLegs, autoPrice, builtLegsWithSymbols]);

  // Keep a ref to the latest fetchQuotes so the polling interval always calls the current version
  const fetchQuotesRef = useRef(fetchQuotes);
  fetchQuotesRef.current = fetchQuotes;

  useEffect(() => {
    if (!open || !quoteAccountId || orderLegs.length === 0) return;
    // Initial fetch
    fetchQuotesRef.current();
    // Poll every 2 seconds
    quoteIntervalRef.current = setInterval(() => {
      fetchQuotesRef.current();
    }, 2000);
    return () => {
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current);
        quoteIntervalRef.current = null;
      }
    };
  }, [open, quoteAccountId, orderLegs]);

  // Fetch underlying quote when symbol changes
  const fetchUnderlyingQuote = useCallback(async () => {
    if (!quoteAccountId || !underlying) {
      setUnderlyingQuote(null);
      return;
    }

    try {
      let symbol = underlying;
      if (symbol === 'SPXW') {
        symbol = '$SPX';
      }

      const response = await apiRequest(`/api/schwab-api/quote?accountId=${quoteAccountId}&symbol=${symbol}`);
      if (response.data?.data) {
        setUnderlyingQuote({
          last: response.data.data.last,
          bid: response.data.data.bid,
          ask: response.data.data.ask,
          change: response.data.data.change,
          percentChange: response.data.data.percentChange,
        });
      }
    } catch (err) {
      console.error('Failed to fetch underlying quote:', err);
      setUnderlyingQuote(null);
    }
  }, [quoteAccountId, underlying]);

  // Fetch underlying quote when underlying changes
  useEffect(() => {
    if (underlying && quoteAccountId) {
      fetchUnderlyingQuote();
    }
  }, [underlying, quoteAccountId, fetchUnderlyingQuote]);

  // Fetch chains data for Greeks (delta, IV, open interest)
  const fetchChainsData = useCallback(async () => {
    if (!selectedAccountId || !underlying) {
      setChainsData({});
      setUnderlyingQuote(null);
      return;
    }

    // Get expiration from orderLegs
    const firstLeg = orderLegs.find(l => l.expiration && l.assetType !== 'EQUITY');
    if (!firstLeg || !firstLeg.expiration) {
      setChainsData({});
      setUnderlyingQuote(null);
      return;
    }

    setChainsLoading(true);
    try {
      // Always use a Schwab account for chains (market data)
      const schwabAcc = accounts.find(a => a.accountType === 'schwab');
      const quoteAccountId = schwabAcc?.id || selectedAccountId;

      // Convert expiration from YYYY-MM-DD to YYYY-MM-DD format (already correct)
      let expDate = firstLeg.expiration;

      // Map SPX/SPXW to $SPX for Schwab API
      let symbol = underlying;
      if (symbol === 'SPXW' || symbol === 'SPX') {
        symbol = '$SPX';
      }

      // Fetch both call and put chains
      const [callResponse, putResponse] = await Promise.all([
        apiRequest(`/api/schwab-api/chains?accountId=${quoteAccountId}&symbol=${symbol}&contractType=CALL&includeUnderlyingQuote=true&strategy=SINGLE&fromDate=${expDate}&toDate=${expDate}`),
        apiRequest(`/api/schwab-api/chains?accountId=${quoteAccountId}&symbol=${symbol}&contractType=PUT&includeUnderlyingQuote=true&strategy=SINGLE&fromDate=${expDate}&toDate=${expDate}`)
      ]);

      const chainsMap = {};

      // Process call chains
      if (callResponse.data?.data?.callExpDateMap) {
        for (const [expKey, strikes] of Object.entries(callResponse.data.data.callExpDateMap)) {
          for (const [strike, options] of Object.entries(strikes)) {
            if (options && options.length > 0) {
              const opt = options[0];
              chainsMap[strike] = {
                ...chainsMap[strike],
                callDelta: opt.delta,
                callIV: opt.volatility,
                callOI: opt.openInterest,
                callMark: opt.mark,
                callBid: opt.bid,
                callAsk: opt.ask
              };
            }
          }
        }
      }

      // Extract underlying quote from response
      if (callResponse.data?.data?.underlying) {
        const underlyingData = callResponse.data.data.underlying;
        setUnderlyingQuote({
          last: underlyingData.last,
          bid: underlyingData.bid,
          ask: underlyingData.ask,
          change: underlyingData.netChange ?? underlyingData.change ?? 0,
          percentChange: underlyingData.netPercentChange ?? underlyingData.percentChange ?? 0
        });
      }

      // Process put chains
      if (putResponse.data?.data?.putExpDateMap) {
        for (const [expKey, strikes] of Object.entries(putResponse.data.data.putExpDateMap)) {
          for (const [strike, options] of Object.entries(strikes)) {
            if (options && options.length > 0) {
              const opt = options[0];
              chainsMap[strike] = {
                ...chainsMap[strike],
                putDelta: opt.delta,
                putIV: opt.volatility,
                putOI: opt.openInterest,
                putMark: opt.mark,
                putBid: opt.bid,
                putAsk: opt.ask
              };
            }
          }
        }
      }

      setChainsData(chainsMap);
    } catch (err) {
      console.error('Failed to fetch chains data:', err);
      setChainsData({});
      setUnderlyingQuote(null);
    } finally {
      setChainsLoading(false);
    }
  }, [selectedAccountId, underlying, orderLegs]);

  // Fetch chains when underlying or legs change
  useEffect(() => {
    if (underlying && orderLegs.length > 0 && orderLegs.some(l => l.expiration && l.assetType !== 'EQUITY')) {
      fetchChainsData();
    }
  }, [fetchChainsData]);

  // Prefill the nearest weekly expiration (from the option chain) once an
  // underlying is known — fills only empty expiration fields, so a date
  // supplied by an alert or typed by the user is never overwritten.
  const expPrefillSymbolRef = useRef('');
  useEffect(() => {
    if (!open) return;
    const symbol = (underlying || manualSymbol || '').trim().toUpperCase();
    if (!symbol || expPrefillSymbolRef.current === symbol) return;
    const needsPrefill =
      !manualExpiration ||
      orderLegs.some(l => l.assetType !== 'EQUITY' && !l.expiration) ||
      customLegs.some(l => l.assetType !== 'EQUITY' && !l.expiration);
    if (!needsPrefill) return;
    expPrefillSymbolRef.current = symbol;
    fetchNearestWeeklyExpiration(symbol).then(date => {
      if (!date) return;
      setManualExpiration(prev => prev || date);
      setOrderLegs(prev => prev.map(l => (l.assetType !== 'EQUITY' && !l.expiration) ? { ...l, expiration: date } : l));
      setCustomLegs(prev => prev.map(l => (l.assetType !== 'EQUITY' && !l.expiration) ? { ...l, expiration: date } : l));
    });
  }, [open, underlying, manualSymbol, manualExpiration, orderLegs, customLegs]);

  const handlePlaceOrder = async () => {
    if (!selectedAccountId || orderLegs.length === 0) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      if (paperMode) {
        const paperBody = {
          underlying,
          legs: orderLegs.map(l => ({ ...l, quantity: parseInt(l.quantity) || parseInt(quantity) || 1 })),
          orderType,
          price: orderType === 'MARKET' ? null : (parseFloat(price) || null),
          quantity: parseInt(quantity) || 1,
          takeProfitPct: paperTakeProfitPct ? parseFloat(paperTakeProfitPct) : null,
          stopLossPct: paperStopLossPct ? parseFloat(paperStopLossPct) : null,
        };
        const resp = await apiRequest('/api/paper-trading/quick-trade', {
          method: 'POST',
          body: JSON.stringify(paperBody),
        });
        if (resp.data?.success) {
          setResult({ success: true, orderId: resp.data.orderId, orderType, price: resp.data.fillPrice });
        } else {
          setError(`Paper order rejected: ${resp.data?.error || 'Unknown error'}`);
        }
        return;
      }

      const built = builtLegsWithSymbols();
      const builtLegs = built.map(l => {
        const assetType = l.assetType || (isEquity ? 'EQUITY' : 'OPTION');
        return { symbol: l.symbol, instruction: l.instruction, quantity: parseInt(l.quantity) || parseInt(quantity) || 1, assetType };
      });
      const hasEquityLeg = builtLegs.some(l => l.assetType === 'EQUITY');
      const allEquity = builtLegs.every(l => l.assetType === 'EQUITY');

      let effectiveOrderType = orderType;
      let effectivePrice = parseFloat(price);

      if (builtLegs.length > 1 && !hasEquityLeg) {
        const netMid = calculateNetMid(built, legQuotes);
        if (netMid !== null) {
          if (effectiveOrderType === 'LIMIT') {
            effectiveOrderType = netMid >= 0 ? 'NET_DEBIT' : 'NET_CREDIT';
          }
          if (autoPrice || !effectivePrice || isNaN(effectivePrice)) {
            effectivePrice = Math.abs(Math.round(netMid * 100) / 100);
          }
        }
      }

      const body = {
        accountId: selectedAccountId,
        legs: builtLegs,
        orderType: effectiveOrderType,
        price: effectivePrice,
        session: 'NORMAL',
        duration,
        ...(allEquity ? { assetType: 'EQUITY' } : {})
      };

      // Add stop order parameters
      if (orderType === 'STOP' || orderType === 'STOP_LIMIT') {
        body.stopPrice = parseFloat(stopPrice);
      }
      if (orderType === 'STOP_LIMIT') {
        body.stopLimitPrice = parseFloat(stopLimitPrice);
      }
      if (orderType === 'TRAILING_STOP') {
        body.trailingStopType = trailingStopType;
        body.trailingStopAmount = parseFloat(trailingStopAmount);
        body.trailingStopPercent = trailingStopPercent;
      }

      if (otoEnabled && otoChildLegs.length > 0) {
        const childBuilt = buildLegSymbols(underlying, otoChildLegs);
        const enabledExits = exitOrders.filter(e => e.enabled && e.price);
        
        // Build limit exit orders (always LIMIT for take-profit)
        if (enabledExits.length === 1) {
          // Single exit — use otoChildOrder for backward compat
          body.otoChildOrder = {
            legs: childBuilt.map(l => ({
              symbol: l.symbol,
              instruction: l.instruction,
              quantity: parseInt(enabledExits[0].qty) || 1,
              assetType: l.assetType || (isEquity ? 'EQUITY' : 'OPTION')
            })),
            orderType: 'LIMIT',
            price: parseFloat(enabledExits[0].price) || undefined,
            session: 'NORMAL',
            duration,
          };
        } else if (enabledExits.length > 1) {
          // Multiple exits — use otoChildOrders array
          body.otoChildOrders = enabledExits.map(exit => ({
            legs: childBuilt.map(l => ({
              symbol: l.symbol,
              instruction: l.instruction,
              quantity: parseInt(exit.qty) || 1,
              assetType: l.assetType || (isEquity ? 'EQUITY' : 'OPTION')
            })),
            orderType: 'LIMIT',
            price: parseFloat(exit.price) || undefined,
            session: 'NORMAL',
            duration,
          }));
        }

        // Add OCO stop order if enabled
        if (ocoStopEnabled) {
          const stopOrder = {
            legs: childBuilt.map(l => ({
              symbol: l.symbol,
              instruction: l.instruction,
              quantity: parseInt(quantity) || 1,
              assetType: l.assetType || (isEquity ? 'EQUITY' : 'OPTION')
            })),
            orderType: ocoStopOrderType,
            session: 'NORMAL',
            duration,
          };
          // Add stop order parameters
          if (ocoStopOrderType === 'STOP' || ocoStopOrderType === 'STOP_LIMIT') {
            stopOrder.stopPrice = parseFloat(ocoStopPrice);
          }
          if (ocoStopOrderType === 'STOP_LIMIT') {
            stopOrder.stopLimitPrice = parseFloat(ocoStopLimitPrice);
          }
          if (ocoStopOrderType === 'TRAILING_STOP') {
            stopOrder.trailingStopType = ocoTrailingStopType;
            stopOrder.trailingStopAmount = parseFloat(ocoTrailingStopAmount);
            stopOrder.trailingStopPercent = ocoTrailingStopPercent;
          }
          body.otoStopOrder = stopOrder;
        }
      }

      // Determine account type and route to correct API
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';

      let resp;
      if (isRobinhoodMcp) {
        // Route to Robinhood MCP order placement
        const rhBody = {
          accountId: selectedAccountId,
          legs: builtLegs,
          orderType: effectiveOrderType,
          price: effectivePrice,
          timeInForce: 'gfd',
          ...(allEquity ? { assetType: 'EQUITY' } : {})
        };
        resp = await apiRequest('/api/mcp/robinhood/orders/place', {
          method: 'POST',
          body: JSON.stringify(rhBody),
        });
      } else if (isRobinhoodDirect) {
        // Route to Robinhood direct-API order placement (robin_stocks)
        const rhDirectBody = {
          accountId: selectedAccountId,
          legs: builtLegs,
          orderType: effectiveOrderType,
          price: effectivePrice,
          timeInForce: 'gfd',
          ...(allEquity ? { assetType: 'EQUITY' } : {})
        };
        resp = await apiRequest(`/api/robinhood-api/orders/place/${selectedAccountId}`, {
          method: 'POST',
          body: JSON.stringify(rhDirectBody),
        });
      } else {
        // Route to Schwab order placement
        resp = await apiRequest('/api/schwab-api/orders/place', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }

      if (resp.data?.success) {
        setResult({ success: true, orderId: resp.data.orderId, orderType: effectiveOrderType, price: effectivePrice });
      } else {
        const detail = resp.data?.message || resp.data?.error || resp.data?.details || 'Unknown error';
        setError(`Order rejected: ${detail}`);
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      setError(`Order failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const built = builtLegsWithSymbols();
  const netMid = calculateNetMid(built, legQuotes);

  // Build child (triggered closing) legs by flipping opening instructions to closing
  const otoChildLegs = useMemo(() => {
    if (!otoEnabled || orderLegs.length === 0) return [];
    return orderLegs.map(leg => ({
      ...leg,
      instruction: OPEN_TO_CLOSE[leg.instruction] || leg.instruction,
    }));
  }, [otoEnabled, orderLegs]);

  const isEquity = useMemo(() => {
    return strategy === 'equity' || orderLegs.some(leg => leg.assetType === 'EQUITY');
  }, [strategy, orderLegs]);

  const isOpeningOrder = isEquity || orderLegs.some(leg =>
    leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'SELL_TO_OPEN'
  );

  const isCreditEntry = orderLegs.some(leg => leg.instruction === 'SELL_TO_OPEN');

  // Live payoff-diagram legs for the order being built. Updates as the user
  // edits strike/type/instruction/qty and as the 2s quote poll refreshes mids.
  // cost_basis convention: positive = debit paid (long), negative = credit
  // received (short); PayoffDiagram only uses the signed sum, so how the net
  // premium is distributed across legs doesn't affect the rendered curve.
  const payoffLegs = useMemo(() => {
    const isBuyInstruction = (instr) =>
      instr === 'BUY_TO_OPEN' || instr === 'BUY_TO_CLOSE' || instr === 'BUY' || instr === 'BUY_TO_COVER';
    const optLegs = orderLegs.filter(l => l.assetType !== 'EQUITY' && parseFloat(l.strike) > 0);
    if (!optLegs.length || !underlying) return [];

    const contracts = parseInt(quantity) || 1;
    const entered = parseFloat(price);
    const hasEntered = Number.isFinite(entered) && entered !== 0;
    const enteredNet = hasEntered
      ? (optLegs.length === 1
          ? (isBuyInstruction(optLegs[0].instruction) ? 1 : -1)
          : (orderType === 'NET_CREDIT' ? -1 : orderType === 'NET_DEBIT' ? 1 : (isCreditEntry ? -1 : 1)))
        * Math.abs(entered) * contracts * 100
      : null;

    const legs = optLegs.map(leg => {
      const qty = Math.abs(parseInt(leg.quantity) || contracts) || 1;
      const isLong = isBuyInstruction(leg.instruction);
      let mid = null;
      try {
        const symKey = buildOCCSymbol(underlying, leg.expiration, leg.putCall, parseFloat(leg.strike)).replace(/\s+/g, '');
        const q = legQuotes[symKey];
        if (q && q.mid != null && isFinite(q.mid)) mid = Math.abs(q.mid);
      } catch { /* unbuildable symbol (e.g. missing expiration) — no quote */ }
      return {
        optionInfo: { strike: parseFloat(leg.strike), type: leg.putCall === 'P' ? 'P' : 'C', isOption: true },
        quantity: isLong ? qty : -qty,
        _mid: mid,
        _isLong: isLong,
        _qty: qty,
      };
    });

    const allPriced = legs.every(l => l._mid != null);
    if (allPriced) {
      legs.forEach(l => { l.cost_basis = (l._isLong ? 1 : -1) * l._mid * l._qty * 100; });
    } else if (enteredNet != null) {
      // Distribute the entered net price across legs without mids
      let partial = 0;
      legs.forEach(l => {
        if (l._mid != null) {
          l.cost_basis = (l._isLong ? 1 : -1) * l._mid * l._qty * 100;
          partial += l.cost_basis;
        }
      });
      const unpriced = legs.filter(l => l._mid == null);
      const residual = enteredNet - partial;
      unpriced.forEach(l => { l.cost_basis = residual / unpriced.length; });
    } else {
      legs.forEach(l => { l.cost_basis = l._mid != null ? (l._isLong ? 1 : -1) * l._mid * l._qty * 100 : 0; });
    }
    legs.forEach(l => { delete l._mid; delete l._isLong; delete l._qty; });
    return legs;
  }, [orderLegs, legQuotes, price, quantity, underlying, orderType, isCreditEntry]);

  const payoffLabel = useMemo(() => {
    if (payoffLegs.length === 1) {
      const l = payoffLegs[0];
      return `${l.quantity > 0 ? 'Long' : 'Short'} ${l.optionInfo.type === 'C' ? 'Call' : 'Put'}`;
    }
    if (payoffLegs.length > 1) {
      return manualStrategyConfig?.label && strategy !== 'custom' ? manualStrategyConfig.label : 'Multi-Leg';
    }
    return '';
  }, [payoffLegs, manualStrategyConfig, strategy]);

  const payoffChart = payoffLegs.length > 0 ? (
    <Grid container spacing={2} alignItems="stretch" sx={{ mb: 1.5 }}>
      <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
        <PayoffDiagram
          legs={payoffLegs}
          spreadType={payoffLabel}
          underlyingPrice={underlyingQuote?.last ?? null}
          underlyingSymbol={underlying}
          animated={false}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
        <UnderlyingPriceChart
          symbol={underlying}
          currentPrice={underlyingQuote?.last ?? null}
        />
      </Grid>
    </Grid>
  ) : null;

  // Recalculate exit prices when entry price changes
  const handleEntryPriceChange = (val) => {
    setOtoEntryPrice(val);
    const entry = parseFloat(val);
    if (!Number.isFinite(entry) || entry <= 0) return;
    setExitOrders(prev => prev.map(eo => {
      const ep = isCreditEntry ? entry * (1 - eo.pct / 100) : entry * (1 + eo.pct / 100);
      return { ...eo, price: String(Math.max(0.01, Math.round(ep * 100) / 100)) };
    }));
  };

  // Recalculate exit price for a single exit order when its pct changes
  const handleExitPctChange = (idx, newPct) => {
    const entry = parseFloat(otoEntryPrice);
    let newPrice = '';
    if (Number.isFinite(entry) && entry > 0) {
      const ep = isCreditEntry ? entry * (1 - newPct / 100) : entry * (1 + newPct / 100);
      newPrice = String(Math.max(0.01, Math.round(ep * 100) / 100));
    }
    setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, pct: newPct, price: newPrice } : eo));
  };

  // Distribute exit quantities when total quantity changes
  // First `remainder` exits get `base + 1`, rest get `base`
  // Exits with 0 qty are auto-disabled
  const handleQtyChange = (newQty) => {
    setQuantity(newQty);
    const total = parseInt(newQty) || 0;
    const base = Math.floor(total / 4);
    const remainder = total % 4;
    setExitOrders(prev => prev.map((eo, i) => {
      const exitQty = base + (i < remainder ? 1 : 0);
      return { ...eo, qty: String(exitQty), enabled: exitQty > 0 };
    }));
  };

  const updateLeg = (idx, field, value) => {
    setOrderLegs(prev => {
      const newLegs = [...prev];
      newLegs[idx] = { ...newLegs[idx], [field]: value };
      
      // If setting expiration, propagate to other option legs that don't have a date
      if (field === 'expiration' && value) {
        for (let i = 0; i < newLegs.length; i++) {
          if (i !== idx && !newLegs[i].expiration && newLegs[i].assetType !== 'EQUITY') {
            newLegs[i] = { ...newLegs[i], expiration: value };
          }
        }
      }
      
      return newLegs;
    });
  };

  const handleStrategyChange = (newStrategy) => {
    setStrategy(newStrategy);
    if (newStrategy === 'custom') {
      setOrderLegs([]);
      return;
    }

    const strategyConfig = STRATEGIES.find(s => s.value === newStrategy);
    if (!strategyConfig) return;

    if (newStrategy === 'equity') {
      setOrderLegs([{
        assetType: 'EQUITY',
        symbol: underlying.toUpperCase().trim(),
        instruction: 'BUY',
        quantity: null,
      }]);
      return;
    }

    const newLegs = [];
    const isCredit = strategyConfig.strategyType === 'credit';
    
    for (let i = 0; i < strategyConfig.legs; i++) {
      const side = strategyConfig.sides[i];
      let instruction;
      
      if (strategyConfig.value === 'single_call' || strategyConfig.value === 'single_put') {
        instruction = 'BUY_TO_OPEN';
      } else if (isCredit) {
        // For credit spreads/strategies: first leg is short, rest are long
        instruction = i === 0 ? 'SELL_TO_OPEN' : 'BUY_TO_OPEN';
      } else {
        // For debit spreads: first leg is long, rest are short
        instruction = i === 0 ? 'BUY_TO_OPEN' : 'SELL_TO_OPEN';
      }

      newLegs.push({
        expiration: '',
        strike: '',
        putCall: side,
        instruction: instruction,
        quantity: null,
      });
    }

    setOrderLegs(newLegs);
  };

  if (inline) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h5" component="h1" sx={{ fontSize: '1.1rem' }}>
            Place Trade
          </Typography>
          {alert?.symbol && (
            <Typography variant="caption" color="textSecondary" component="span">
              {alert.symbol} {alert.strategy ? `· ${alert.strategy}` : ''}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={resetForm} disabled={loading}>
            Clear
          </Button>
          <Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={() => { setSaveName(generateSaveName()); setSaveDialogOpen(true); }} disabled={loading}>
            Save
          </Button>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value=""
              displayEmpty
              renderValue={() => savedTradesLoading ? 'Loading...' : 'Load Saved'}
              onChange={(e) => { if (e.target.value) handleLoadTrade(e.target.value); }}
              disabled={loading || savedTrades.length === 0}
              sx={{ fontSize: '0.8rem', height: 32 }}
            >
              {savedTrades.map(t => (
                <MenuItem key={t.id} value={t.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t.name}</span>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteSavedTrade(t.id); }} sx={{ p: 0.25 }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {saveDialogOpen && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Trade name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              sx={{ flexGrow: 1 }}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTrade(); }}
            />
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveTrade} disabled={!saveName.trim()}>
              Save
            </Button>
            <Button size="small" onClick={() => { setSaveDialogOpen(false); setSaveName(''); }}>
              Cancel
            </Button>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.85rem' }}>{error}</Alert>}
        {result?.success && <Alert severity="success" sx={{ mb: 1.5, fontSize: '0.85rem' }}>Order placed! ID: {result.orderId} ({result.orderType}{result.price != null ? ` @ $${result.price}` : ''})</Alert>}

        {/* Trade Alert Paste - optional quick paste */}
        {showInputSection && (
          <Box sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<ContentPasteIcon />} onClick={handlePasteFromClipboard} disabled={parsingImage || parsingAI}>
                  Paste from Clipboard
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={parsingImage ? <CircularProgress size={14} /> : <ImageIcon />}
                  onClick={handlePasteScreenshotClick}
                  disabled={parsingImage || parsingAI}
                  title="Paste an order-entry screenshot — or press Ctrl+V in the box below"
                >
                  {parsingImage ? 'Parsing…' : 'Paste Screenshot'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={parsingAI ? <CircularProgress size={14} /> : <SendIcon />}
                  onClick={handleParseAlert}
                  disabled={!rawText.trim() || parsingImage || parsingAI}
                  title="Parses locally first; falls back to AI if the format isn't recognized"
                >
                  {parsingAI ? 'AI Parsing…' : 'Parse & Load'}
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={2}
                size="small"
                label="Paste trade alert text or screenshot here (optional)..."
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                onPaste={handleAlertPaste}
                placeholder="e.g. STO | SPX | 7535C & 7545C | 7/23 exp | 1.15 cr"
              />
              {inputError && <Alert severity="error" sx={{ mt: 1, fontSize: '0.85rem' }}>{inputError}</Alert>}
            </Paper>
          </Box>
        )}

        {/* Account, Strategy, Underlying & Qty */}
        {/* mt leaves room for the floating labels — DialogContent has paddingTop:0 when it follows DialogTitle, and its overflow-y:auto clips them */}
        <Grid container spacing={1} sx={{ mb: 1.5, mt: 1.5 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Account</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                label="Account"
                disabled={accountsLoading}
              >
                {accounts.map(acc => {
                  const typeLabel =
                    acc.accountType === 'schwab' ? 'Schwab' :
                    acc.accountType === 'robinhood-mcp' ? 'Robinhood' :
                    acc.accountType === 'paper' ? 'Paper' : '';
                  return (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name || acc.id} {acc.nickname ? `(${acc.nickname})` : ''} {typeLabel ? `(${typeLabel})` : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Strategy</InputLabel>
              <Select
                value={strategy}
                onChange={(e) => handleStrategyChange(e.target.value)}
                label="Strategy"
                disabled={loading}
              >
                <MenuItem value="custom">Custom (Build Your Own)</MenuItem>
                <MenuItem value="single_call">Single Call</MenuItem>
                <MenuItem value="single_put">Single Put</MenuItem>
                <MenuItem value="ccs">Call Credit Spread</MenuItem>
                <MenuItem value="pcs">Put Credit Spread</MenuItem>
                <MenuItem value="cds">Call Debit Spread</MenuItem>
                <MenuItem value="pds">Put Debit Spread</MenuItem>
                <MenuItem value="strangle">Strangle</MenuItem>
                <MenuItem value="straddle">Straddle</MenuItem>
                <MenuItem value="iron_condor">Iron Condor</MenuItem>
                <MenuItem value="equity">Equity Shares</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth size="small"
              label="Underlying"
              value={underlying}
              onChange={(e) => setUnderlying(e.target.value.toUpperCase())}
              disabled={loading}
            />
            {underlyingQuote && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                <span style={{ fontWeight: 'bold' }}>${underlyingQuote.last?.toFixed(2)}</span>
                {' '}B: ${underlyingQuote.bid?.toFixed(2)} A: ${underlyingQuote.ask?.toFixed(2)}
                {' '}({(underlyingQuote.change ?? 0) >= 0 ? '+' : ''}{(underlyingQuote.change ?? 0).toFixed(2)} / {(underlyingQuote.percentChange ?? 0) >= 0 ? '+' : ''}{(underlyingQuote.percentChange ?? 0).toFixed(2)}%)
              </Typography>
            )}
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth size="small"
              type="number"
              label="Default Qty"
              value={quantity}
              onChange={(e) => handleQtyChange(e.target.value)}
              disabled={loading}
              sx={{ '& input[type=number]': { MozAppearance: 'textfield' }, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
            />
          </Grid>
        </Grid>

        {/* Legs — compact single-line layout */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Legs</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" variant="outlined" onClick={() => setOrderLegs(prev => [...prev, { assetType: 'OPTION', expiration: '', strike: '', putCall: 'C', instruction: 'BUY_TO_OPEN', quantity: null }])} disabled={loading}>
                + Option Leg
              </Button>
              <Button size="small" variant="outlined" onClick={() => setOrderLegs(prev => [...prev, { assetType: 'EQUITY', symbol: underlying.toUpperCase().trim(), instruction: 'BUY', quantity: null }])} disabled={loading}>
                + Equity Leg
              </Button>
            </Box>
          </Box>
          {orderLegs.length > 0 && orderLegs.map((leg, idx) => {
              const legIsEquity = leg.assetType ? leg.assetType === 'EQUITY' : isEquity;
              const sym = legIsEquity
                ? (leg.symbol || underlying).toUpperCase().trim()
                : (underlying ? buildOCCSymbol(underlying, leg.expiration, leg.putCall, parseFloat(leg.strike)) : '');
              const symKey = sym.replace(/\s+/g, '');
              const q = legQuotes[symKey];
              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <IconButton size="small" onClick={() => setOrderLegs(prev => prev.filter((_, i) => i !== idx))} disabled={loading} sx={{ p: 0.5 }}>
                    ×
                  </IconButton>
                  <FormControl size="small" sx={{ minWidth: 70 }}>
                    <Select
                      value={leg.assetType || (isEquity ? 'EQUITY' : 'OPTION')}
                      onChange={(e) => updateLeg(idx, 'assetType', e.target.value)}
                      disabled={loading}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      <MenuItem value="OPTION">Option</MenuItem>
                      <MenuItem value="EQUITY">Equity</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={leg.instruction || ''}
                      onChange={(e) => updateLeg(idx, 'instruction', e.target.value)}
                      disabled={loading}
                      sx={{ fontSize: '0.85rem' }}
                      displayEmpty
                      renderValue={(val) => INSTRUCTION_LABELS[val] || val || ''}
                    >
                      {legIsEquity ? ([
                          <MenuItem key="buy" value="BUY">Buy</MenuItem>,
                          <MenuItem key="sell" value="SELL">Sell</MenuItem>,
                          <MenuItem key="btc" value="BUY_TO_COVER">Buy to Cover</MenuItem>,
                          <MenuItem key="ss" value="SELL_SHORT">Sell Short</MenuItem>
                        ]) : ([
                          <MenuItem key="bto" value="BUY_TO_OPEN">Buy to Open</MenuItem>,
                          <MenuItem key="sto" value="SELL_TO_OPEN">Sell to Open</MenuItem>,
                          <MenuItem key="btc" value="BUY_TO_CLOSE">Buy to Close</MenuItem>,
                          <MenuItem key="stc" value="SELL_TO_CLOSE">Sell to Close</MenuItem>
                        ])}
                    </Select>
                  </FormControl>
                  {legIsEquity ? (
                    <TextField
                      size="small"
                      value={leg.symbol || ''}
                      onChange={(e) => updateLeg(idx, 'symbol', e.target.value.toUpperCase())}
                      disabled={loading}
                      sx={{ width: 85, '& input': { fontSize: '0.85rem' } }}
                      placeholder="Symbol"
                    />
                  ) : (
                    <>
                      <FormControl size="small" sx={{ minWidth: 70 }}>
                        <Select
                          value={leg.putCall || ''}
                          onChange={(e) => updateLeg(idx, 'putCall', e.target.value)}
                          disabled={loading}
                          sx={{ fontSize: '0.85rem' }}
                          displayEmpty
                          renderValue={(val) => val === 'C' ? 'Call' : val === 'P' ? 'Put' : val || ''}
                        >
                          <MenuItem value="C">Call</MenuItem>
                          <MenuItem value="P">Put</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        value={leg.strike || ''}
                        onChange={(e) => updateLeg(idx, 'strike', e.target.value)}
                        disabled={loading}
                        sx={{ width: 80, '& input': { fontSize: '0.85rem' } }}
                        placeholder="Strike"
                      />
                      <TextField
                        size="small"
                        type="date"
                        value={leg.expiration || ''}
                        onChange={(e) => updateLeg(idx, 'expiration', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={loading}
                        sx={{ width: 130, '& input': { fontSize: '0.85rem' } }}
                      />
                    </>
                  )}
                  <TextField
                    size="small"
                    type="number"
                    value={leg.quantity || quantity}
                    onChange={(e) => updateLeg(idx, 'quantity', e.target.value)}
                    disabled={loading}
                    sx={{
                      width: 72,
                      '& input': { fontSize: '0.85rem' },
                      '& input[type=number]': { MozAppearance: 'textfield' },
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                    }}
                    placeholder="Qty"
                  />
                  <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                    {q && (
                      <>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>B:{q.bid?.toFixed(2)}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>A:{q.ask?.toFixed(2)}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'primary.main' }}>M:{q.mid?.toFixed(2)}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
        {/* Net Mid - right justified below legs */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mr: 0.5, fontSize: '1.1rem' }}>
            {netMid !== null ? (netMid >= 0 ? 'DB' : 'CR') : '—'}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.1rem' }}>
            {netMid !== null ? Math.abs(netMid).toFixed(2) : ''}
          </Typography>
        </Box>
        {/* Order Parameters - single line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order Type</InputLabel>
            <Select
              value={orderType}
              label="Order Type"
              onChange={(e) => setOrderType(e.target.value)}
              disabled={loading}
              sx={{ fontSize: '0.75rem', height: 32 }}
            >
              <MenuItem value="LIMIT">Limit</MenuItem>
              <MenuItem value="MARKET">Market</MenuItem>
              <MenuItem value="STOP">Stop</MenuItem>
              <MenuItem value="STOP_LIMIT">Stop Limit</MenuItem>
              <MenuItem value="TRAILING_STOP">Trailing Stop</MenuItem>
              {orderLegs.length > 1 && <MenuItem value="NET_DEBIT">Net Debit</MenuItem>}
              {orderLegs.length > 1 && <MenuItem value="NET_CREDIT">Net Credit</MenuItem>}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Time in Force</InputLabel>
            <Select
              value={duration}
              label="Time in Force"
              onChange={(e) => setDuration(e.target.value)}
              disabled={loading}
              sx={{ fontSize: '0.75rem', height: 32 }}
            >
              <MenuItem value="DAY">Day</MenuItem>
              <MenuItem value="GOOD_TILL_CANCEL">GTC</MenuItem>
              <MenuItem value="GOOD_TILL_DATE">GTD</MenuItem>
              <MenuItem value="FILL_OR_KILL">Fill or Kill</MenuItem>
            </Select>
          </FormControl>

          {(orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT') && (() => {
            // SPX family trades in $0.05 increments; everything else uses
            // cents. Steppers snap to the nearest valid tick.
            const isSpx = /^\$?SPXW?/i.test(String(underlying || '').replace(/\s+/g, ''));
            const step = isSpx ? 0.05 : 0.01;
            const bump = (dir) => {
              const val = parseFloat(price) || 0;
              const ticks = dir > 0
                ? Math.floor(val / step + 1e-6) + 1
                : Math.ceil(val / step - 1e-6) - 1;
              setPrice((ticks * step).toFixed(2));
              setAutoPrice(false);
            };
            return (
            <TextField
              size="small"
              type="number"
              label={orderLegs.length > 1 ? 'Net Price' : 'Limit Price'}
              value={price}
              onChange={(e) => { setPrice(e.target.value); setAutoPrice(false); }}
              disabled={loading}
              placeholder="e.g. 1.50"
              sx={{
                width: 170,
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
              }}
              slotProps={{
                htmlInput: { step },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title={autoPrice ? 'Auto from mid. Click to lock.' : 'Manual price. Click to auto.'}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => {
                            if (!autoPrice) {
                              // When enabling auto, reset to current mid
                              const built = builtLegsWithSymbols();
                              const netMid = calculateNetMid(built, legQuotes);
                              if (netMid !== null) {
                                setPrice(String(Math.round(Math.abs(netMid) * 100) / 100));
                              }
                            }
                            setAutoPrice(!autoPrice);
                          }}
                        >
                          {autoPrice ? <LockOpenIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => bump(1)}
                        >
                          <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, mt: -1 }}
                          onClick={() => bump(-1)}
                        >
                          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
            />
            );
          })()}
          {paperMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                OTO
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Exit %"
                value={paperTakeProfitPct}
                onChange={(e) => setPaperTakeProfitPct(e.target.value)}
                disabled={loading}
                placeholder="%"
                sx={{ width: 80, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
              />
              <TextField
                size="small"
                type="number"
                label="Stop %"
                value={paperStopLossPct}
                onChange={(e) => setPaperStopLossPct(e.target.value)}
                disabled={loading}
                placeholder="%"
                sx={{ width: 80, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
              />
            </Box>
          )}
          {(orderType === 'STOP' || orderType === 'STOP_LIMIT') && (
            <TextField
              size="small"
              type="number"
              label="Stop Price"
              value={stopPrice}
              onChange={(e) => setStopPrice(e.target.value)}
              disabled={loading}
              placeholder="e.g. 680.00"
              sx={{ width: 100, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
            />
          )}
          {orderType === 'STOP_LIMIT' && (
            <TextField
              size="small"
              type="number"
              label="Stop Limit Price"
              value={stopLimitPrice}
              onChange={(e) => setStopLimitPrice(e.target.value)}
              disabled={loading}
              placeholder="e.g. 679.50"
              sx={{ width: 100, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
            />
          )}
          {orderType === 'TRAILING_STOP' && (
            <>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <InputLabel>Trail Type</InputLabel>
                <Select
                  value={trailingStopType}
                  label="Trail Type"
                  onChange={(e) => setTrailingStopType(e.target.value)}
                  disabled={loading}
                  sx={{ fontSize: '0.75rem', height: 32 }}
                >
                  <MenuItem value="MARKET">Market</MenuItem>
                  <MenuItem value="LIMIT">Limit</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Trail Amount"
                value={trailingStopAmount}
                onChange={(e) => setTrailingStopAmount(e.target.value)}
                disabled={loading}
                placeholder="e.g. 1.00"
                sx={{ width: 80, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={trailingStopPercent}
                    onChange={(e) => setTrailingStopPercent(e.target.checked)}
                    size="small"
                    disabled={loading}
                  />
                }
                label="Percent"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              />
            </>
          )}
          {quotesLoading && <CircularProgress size={14} />}
        </Box>
        {/* Live payoff diagram — updates as legs/price change */}
        {payoffChart}

        {isOpeningOrder && (
          <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={otoEnabled}
                    onChange={(e) => setOtoEnabled(e.target.checked)}
                    size="small"
                    disabled={loading}
                  />
                }
                label="One Triggers Other — auto-place exit orders when entry fills"
              />
            </Box>
            {otoEnabled && (
              <>
                <Grid container spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Entry Price"
                      value={otoEntryPrice}
                      onChange={(e) => handleEntryPriceChange(e.target.value)}
                      disabled={loading}
                      placeholder="e.g. 1.00"
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth size="small"
                      type="number"
                      label="Total Qty"
                      value={quantity}
                      onChange={(e) => handleQtyChange(e.target.value)}
                      disabled={loading}
                      sx={{ '& input': { fontSize: '0.85rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Distributed across {Math.min(4, parseInt(quantity) || 0)} exit(s)
                    </Typography>
                  </Grid>
                </Grid>

                {/* Exit Orders */}
                <Box sx={{ mt: 1.5, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                    Exit Orders (triggered when entry fills)
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    {otoChildLegs.map((leg, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        <Typography variant="caption" fontWeight="bold">Leg {idx + 1}</Typography>
                        <Chip label={leg.instruction.replace(/_/g, ' ')} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {leg.putCall === 'C' ? 'Call' : 'Put'} @ {leg.strike} · {leg.expiration}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {exitOrders.map((exit, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Switch
                        checked={exit.enabled}
                        onChange={(e) => setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, enabled: e.target.checked } : eo))}
                        size="small"
                        disabled={loading || parseInt(exit.qty) === 0}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={`Gain %`}
                        value={exit.pct}
                        onChange={(e) => handleExitPctChange(idx, parseInt(e.target.value) || 0)}
                        disabled={loading || !exit.enabled}
                        sx={{ width: 80, '& input': { fontSize: '0.85rem' } }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Qty"
                        value={exit.qty}
                        onChange={(e) => setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, qty: e.target.value } : eo))}
                        disabled={loading || !exit.enabled}
                        sx={{ width: 70, '& input': { fontSize: '0.85rem' } }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Limit Price"
                        value={exit.price}
                        onChange={(e) => setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, price: e.target.value } : eo))}
                        disabled={loading || !exit.enabled}
                        sx={{ width: 100, '& input': { fontSize: '0.85rem' } }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        LIMIT
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* OCO Stop Order */}
                <Box sx={{ mt: 1, p: 1, border: 1, borderColor: 'warning.main', borderRadius: 1, backgroundColor: 'warning.light' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      OCO Stop Order (cancels limit exits when triggered)
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ocoStopEnabled}
                          onChange={(e) => setOcoStopEnabled(e.target.checked)}
                          size="small"
                          disabled={loading}
                        />
                      }
                      label="Enable"
                    />
                  </Box>
                  {ocoStopEnabled && (
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={6} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Stop Order Type</InputLabel>
                          <Select
                            value={ocoStopOrderType}
                            label="Stop Order Type"
                            onChange={(e) => setOcoStopOrderType(e.target.value)}
                            disabled={loading}
                            sx={{ fontSize: '0.85rem' }}
                          >
                            <MenuItem value="STOP">Stop</MenuItem>
                            <MenuItem value="STOP_LIMIT">Stop Limit</MenuItem>
                            <MenuItem value="TRAILING_STOP">Trailing Stop</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {(ocoStopOrderType === 'STOP' || ocoStopOrderType === 'STOP_LIMIT') && (
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth size="small"
                            type="number"
                            label="Stop Price"
                            value={ocoStopPrice}
                            onChange={(e) => setOcoStopPrice(e.target.value)}
                            disabled={loading}
                            placeholder="e.g. 680.00"
                            sx={{ '& input': { fontSize: '0.85rem' } }}
                          />
                        </Grid>
                      )}
                      {ocoStopOrderType === 'STOP_LIMIT' && (
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth size="small"
                            type="number"
                            label="Stop Limit Price"
                            value={ocoStopLimitPrice}
                            onChange={(e) => setOcoStopLimitPrice(e.target.value)}
                            disabled={loading}
                            placeholder="e.g. 679.50"
                            sx={{ '& input': { fontSize: '0.85rem' } }}
                          />
                        </Grid>
                      )}
                      {ocoStopOrderType === 'TRAILING_STOP' && (
                        <>
                          <Grid item xs={6} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Trail Type</InputLabel>
                              <Select
                                value={ocoTrailingStopType}
                                label="Trail Type"
                                onChange={(e) => setOcoTrailingStopType(e.target.value)}
                                disabled={loading}
                                sx={{ fontSize: '0.85rem' }}
                              >
                                <MenuItem value="MARKET">Market</MenuItem>
                                <MenuItem value="LIMIT">Limit</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth size="small"
                              type="number"
                              label="Trail Amount"
                              value={ocoTrailingStopAmount}
                              onChange={(e) => setOcoTrailingStopAmount(e.target.value)}
                              disabled={loading}
                              placeholder="e.g. 1.00"
                              sx={{ '& input': { fontSize: '0.85rem' } }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={ocoTrailingStopPercent}
                                  onChange={(e) => setOcoTrailingStopPercent(e.target.checked)}
                                  size="small"
                                  disabled={loading}
                                />
                              }
                              label="Percent"
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, pb: 2 }}>
          {onClose && (
            <Button onClick={onClose} disabled={loading} size="small">Close</Button>
          )}
          <Button
            onClick={handlePlaceOrder}
            variant="contained"
            color="primary"
            disabled={loading || !selectedAccountId || orderLegs.length === 0}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            size="small"
          >
            {loading ? 'Placing...' : result?.success ? 'Place Another Order' : 'Place Order'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={payoffLegs.length > 0 ? 'md' : 'sm'} fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: '1.1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        Place Trade
        {alert?.symbol && (
          <Typography variant="caption" color="textSecondary" component="span">
            {alert.symbol} {alert.strategy ? `· ${alert.strategy}` : ''}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={resetForm} disabled={loading}>
          Clear
        </Button>
        <Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={() => { setSaveName(generateSaveName()); setSaveDialogOpen(true); }} disabled={loading}>
          Save
        </Button>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value=""
            displayEmpty
            renderValue={() => savedTradesLoading ? 'Loading...' : 'Load Saved'}
            onChange={(e) => { if (e.target.value) handleLoadTrade(e.target.value); }}
            disabled={loading || savedTrades.length === 0}
            sx={{ fontSize: '0.8rem', height: 32 }}
          >
            {savedTrades.map(t => (
              <MenuItem key={t.id} value={t.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t.name}</span>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteSavedTrade(t.id); }} sx={{ p: 0.25 }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogTitle>
      {saveDialogOpen && (
        <Box sx={{ display: 'flex', gap: 1, px: 3, pb: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Trade name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            sx={{ flexGrow: 1 }}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTrade(); }}
          />
          <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveTrade} disabled={!saveName.trim()}>
            Save
          </Button>
          <Button size="small" onClick={() => { setSaveDialogOpen(false); setSaveName(''); }}>
            Cancel
          </Button>
        </Box>
      )}
      <DialogContent sx={{ pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.85rem' }}>{error}</Alert>}
        {result?.success && <Alert severity="success" sx={{ mb: 1.5, fontSize: '0.85rem' }}>Order placed! ID: {result.orderId} ({result.orderType}{result.price != null ? ` @ $${result.price}` : ''})</Alert>}

        {/* Trade Alert Paste - optional quick paste */}
        {showInputSection && (
          <Box sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<ContentPasteIcon />} onClick={handlePasteFromClipboard} disabled={parsingImage || parsingAI}>
                  Paste from Clipboard
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={parsingImage ? <CircularProgress size={14} /> : <ImageIcon />}
                  onClick={handlePasteScreenshotClick}
                  disabled={parsingImage || parsingAI}
                  title="Paste an order-entry screenshot — or press Ctrl+V in the box below"
                >
                  {parsingImage ? 'Parsing…' : 'Paste Screenshot'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={parsingAI ? <CircularProgress size={14} /> : <SendIcon />}
                  onClick={handleParseAlert}
                  disabled={!rawText.trim() || parsingImage || parsingAI}
                  title="Parses locally first; falls back to AI if the format isn't recognized"
                >
                  {parsingAI ? 'AI Parsing…' : 'Parse & Load'}
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={2}
                size="small"
                label="Paste trade alert text or screenshot here (optional)..."
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                onPaste={handleAlertPaste}
                placeholder="e.g. STO | SPX | 7535C & 7545C | 7/23 exp | 1.15 cr"
              />
              {inputError && <Alert severity="error" sx={{ mt: 1, fontSize: '0.85rem' }}>{inputError}</Alert>}
            </Paper>
          </Box>
        )}

        {/* Account, Strategy, Underlying & Qty */}
        {/* mt leaves room for the floating labels — DialogContent has paddingTop:0 when it follows DialogTitle, and its overflow-y:auto clips them */}
        <Grid container spacing={1} sx={{ mb: 1.5, mt: 1.5 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Account</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                label="Account"
                disabled={accountsLoading}
              >
                {accounts.map(acc => {
                  const typeLabel =
                    acc.accountType === 'schwab' ? 'Schwab' :
                    acc.accountType === 'robinhood-mcp' ? 'Robinhood' :
                    acc.accountType === 'paper' ? 'Paper' : '';
                  return (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name || acc.id} {acc.nickname ? `(${acc.nickname})` : ''} {typeLabel ? `(${typeLabel})` : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Strategy</InputLabel>
              <Select
                value={strategy}
                onChange={(e) => handleStrategyChange(e.target.value)}
                label="Strategy"
                disabled={loading}
              >
                <MenuItem value="custom">Custom (Build Your Own)</MenuItem>
                <MenuItem value="single_call">Single Call</MenuItem>
                <MenuItem value="single_put">Single Put</MenuItem>
                <MenuItem value="ccs">Call Credit Spread</MenuItem>
                <MenuItem value="pcs">Put Credit Spread</MenuItem>
                <MenuItem value="cds">Call Debit Spread</MenuItem>
                <MenuItem value="pds">Put Debit Spread</MenuItem>
                <MenuItem value="strangle">Strangle</MenuItem>
                <MenuItem value="straddle">Straddle</MenuItem>
                <MenuItem value="iron_condor">Iron Condor</MenuItem>
                <MenuItem value="equity">Equity Shares</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth size="small"
              label="Underlying"
              value={underlying}
              onChange={(e) => setUnderlying(e.target.value.toUpperCase())}
              disabled={loading}
            />
            {underlyingQuote && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                <span style={{ fontWeight: 'bold' }}>${underlyingQuote.last?.toFixed(2)}</span>
                {' '}B: ${underlyingQuote.bid?.toFixed(2)} A: ${underlyingQuote.ask?.toFixed(2)}
                {' '}({(underlyingQuote.change ?? 0) >= 0 ? '+' : ''}{(underlyingQuote.change ?? 0).toFixed(2)} / {(underlyingQuote.percentChange ?? 0) >= 0 ? '+' : ''}{(underlyingQuote.percentChange ?? 0).toFixed(2)}%)
              </Typography>
            )}
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth size="small"
              type="number"
              label="Default Qty"
              value={quantity}
              onChange={(e) => handleQtyChange(e.target.value)}
              disabled={loading}
              sx={{ '& input[type=number]': { MozAppearance: 'textfield' }, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
            />
          </Grid>
        </Grid>

        {/* Legs — compact single-line layout */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Legs</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" variant="outlined" onClick={() => setOrderLegs(prev => [...prev, { assetType: 'OPTION', expiration: '', strike: '', putCall: 'C', instruction: 'BUY_TO_OPEN', quantity: null }])} disabled={loading}>
                + Option Leg
              </Button>
              <Button size="small" variant="outlined" onClick={() => setOrderLegs(prev => [...prev, { assetType: 'EQUITY', symbol: underlying.toUpperCase().trim(), instruction: 'BUY', quantity: null }])} disabled={loading}>
                + Equity Leg
              </Button>
            </Box>
          </Box>
          {orderLegs.length > 0 && orderLegs.map((leg, idx) => {
              const legIsEquity = leg.assetType ? leg.assetType === 'EQUITY' : isEquity;
              const sym = legIsEquity
                ? (leg.symbol || underlying).toUpperCase().trim()
                : (underlying ? buildOCCSymbol(underlying, leg.expiration, leg.putCall, parseFloat(leg.strike)) : '');
              const symKey = sym.replace(/\s+/g, '');
              const q = legQuotes[symKey];
              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <IconButton size="small" onClick={() => setOrderLegs(prev => prev.filter((_, i) => i !== idx))} disabled={loading} sx={{ p: 0.5 }}>
                    ×
                  </IconButton>
                  <FormControl size="small" sx={{ minWidth: 70 }}>
                    <Select
                      value={leg.assetType || (isEquity ? 'EQUITY' : 'OPTION')}
                      onChange={(e) => updateLeg(idx, 'assetType', e.target.value)}
                      disabled={loading}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      <MenuItem value="OPTION">Option</MenuItem>
                      <MenuItem value="EQUITY">Equity</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={leg.instruction || ''}
                      onChange={(e) => updateLeg(idx, 'instruction', e.target.value)}
                      disabled={loading}
                      sx={{ fontSize: '0.85rem' }}
                      displayEmpty
                      renderValue={(val) => INSTRUCTION_LABELS[val] || val || ''}
                    >
                      {legIsEquity ? ([
                          <MenuItem key="buy" value="BUY">Buy</MenuItem>,
                          <MenuItem key="sell" value="SELL">Sell</MenuItem>,
                          <MenuItem key="btc" value="BUY_TO_COVER">Buy to Cover</MenuItem>,
                          <MenuItem key="ss" value="SELL_SHORT">Sell Short</MenuItem>
                        ]) : ([
                          <MenuItem key="bto" value="BUY_TO_OPEN">Buy to Open</MenuItem>,
                          <MenuItem key="sto" value="SELL_TO_OPEN">Sell to Open</MenuItem>,
                          <MenuItem key="btc" value="BUY_TO_CLOSE">Buy to Close</MenuItem>,
                          <MenuItem key="stc" value="SELL_TO_CLOSE">Sell to Close</MenuItem>
                        ])}
                    </Select>
                  </FormControl>
                  {legIsEquity ? (
                    <TextField
                      size="small"
                      value={leg.symbol || ''}
                      onChange={(e) => updateLeg(idx, 'symbol', e.target.value.toUpperCase())}
                      disabled={loading}
                      sx={{ width: 85, '& input': { fontSize: '0.85rem' } }}
                      placeholder="Symbol"
                    />
                  ) : (
                    <>
                      <FormControl size="small" sx={{ minWidth: 70 }}>
                        <Select
                          value={leg.putCall || ''}
                          onChange={(e) => updateLeg(idx, 'putCall', e.target.value)}
                          disabled={loading}
                          sx={{ fontSize: '0.85rem' }}
                          displayEmpty
                          renderValue={(val) => val === 'C' ? 'Call' : val === 'P' ? 'Put' : val || ''}
                        >
                          <MenuItem value="C">Call</MenuItem>
                          <MenuItem value="P">Put</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        value={leg.strike || ''}
                        onChange={(e) => updateLeg(idx, 'strike', e.target.value)}
                        disabled={loading}
                        sx={{ width: 80, '& input': { fontSize: '0.85rem' } }}
                        placeholder="Strike"
                      />
                      <TextField
                        size="small"
                        type="date"
                        value={leg.expiration || ''}
                        onChange={(e) => updateLeg(idx, 'expiration', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={loading}
                        sx={{ width: 130, '& input': { fontSize: '0.85rem' } }}
                      />
                    </>
                  )}
                  <TextField
                    size="small"
                    type="number"
                    value={leg.quantity || quantity}
                    onChange={(e) => updateLeg(idx, 'quantity', e.target.value)}
                    disabled={loading}
                    sx={{
                      width: 72,
                      '& input': { fontSize: '0.85rem' },
                      '& input[type=number]': { MozAppearance: 'textfield' },
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                    }}
                    placeholder="Qty"
                  />
                  <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                    {q && (
                      <>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>B:{q.bid?.toFixed(2)}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>A:{q.ask?.toFixed(2)}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'primary.main' }}>M:{q.mid?.toFixed(2)}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
        {/* Net Mid - right justified below legs */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mr: 0.5, fontSize: '1.1rem' }}>
            {netMid !== null ? (netMid >= 0 ? 'DB' : 'CR') : '—'}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.1rem' }}>
            {netMid !== null ? Math.abs(netMid).toFixed(2) : ''}
          </Typography>
        </Box>
        {/* Order Parameters - single line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order Type</InputLabel>
            <Select
              value={orderType}
              label="Order Type"
              onChange={(e) => setOrderType(e.target.value)}
              disabled={loading}
              sx={{ fontSize: '0.75rem', height: 32 }}
            >
              <MenuItem value="LIMIT">Limit</MenuItem>
              <MenuItem value="MARKET">Market</MenuItem>
              <MenuItem value="STOP">Stop</MenuItem>
              <MenuItem value="STOP_LIMIT">Stop Limit</MenuItem>
              <MenuItem value="TRAILING_STOP">Trailing Stop</MenuItem>
              {orderLegs.length > 1 && <MenuItem value="NET_DEBIT">Net Debit</MenuItem>}
              {orderLegs.length > 1 && <MenuItem value="NET_CREDIT">Net Credit</MenuItem>}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Time in Force</InputLabel>
            <Select
              value={duration}
              label="Time in Force"
              onChange={(e) => setDuration(e.target.value)}
              disabled={loading}
              sx={{ fontSize: '0.75rem', height: 32 }}
            >
              <MenuItem value="DAY">Day</MenuItem>
              <MenuItem value="GOOD_TILL_CANCEL">GTC</MenuItem>
              <MenuItem value="GOOD_TILL_DATE">GTD</MenuItem>
              <MenuItem value="FILL_OR_KILL">Fill or Kill</MenuItem>
            </Select>
          </FormControl>

          {(orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT') && (() => {
            // SPX family trades in $0.05 increments; everything else uses
            // cents. Steppers snap to the nearest valid tick.
            const isSpx = /^\$?SPXW?/i.test(String(underlying || '').replace(/\s+/g, ''));
            const step = isSpx ? 0.05 : 0.01;
            const bump = (dir) => {
              const val = parseFloat(price) || 0;
              const ticks = dir > 0
                ? Math.floor(val / step + 1e-6) + 1
                : Math.ceil(val / step - 1e-6) - 1;
              setPrice((ticks * step).toFixed(2));
              setAutoPrice(false);
            };
            return (
            <TextField
              size="small"
              type="number"
              label={orderLegs.length > 1 ? 'Net Price' : 'Limit Price'}
              value={price}
              onChange={(e) => { setPrice(e.target.value); setAutoPrice(false); }}
              disabled={loading}
              placeholder="e.g. 1.50"
              sx={{
                width: 170,
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
              }}
              slotProps={{
                htmlInput: { step },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title={autoPrice ? 'Auto from mid. Click to lock.' : 'Manual price. Click to auto.'}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => {
                            if (!autoPrice) {
                              // When enabling auto, reset to current mid
                              const built = builtLegsWithSymbols();
                              const netMid = calculateNetMid(built, legQuotes);
                              if (netMid !== null) {
                                setPrice(String(Math.round(Math.abs(netMid) * 100) / 100));
                              }
                            }
                            setAutoPrice(!autoPrice);
                          }}
                        >
                          {autoPrice ? <LockOpenIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => bump(1)}
                        >
                          <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, mt: -1 }}
                          onClick={() => bump(-1)}
                        >
                          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
            />
            );
          })()}
          {paperMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                OTO
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Exit %"
                value={paperTakeProfitPct}
                onChange={(e) => setPaperTakeProfitPct(e.target.value)}
                disabled={loading}
                placeholder="%"
                sx={{ width: 80, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
              />
              <TextField
                size="small"
                type="number"
                label="Stop %"
                value={paperStopLossPct}
                onChange={(e) => setPaperStopLossPct(e.target.value)}
                disabled={loading}
                placeholder="%"
                sx={{ width: 80, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
              />
            </Box>
          )}
          {(orderType === 'STOP' || orderType === 'STOP_LIMIT') && (
            <TextField
              size="small"
              type="number"
              label="Stop Price"
              value={stopPrice}
              onChange={(e) => setStopPrice(e.target.value)}
              disabled={loading}
              placeholder="e.g. 680.00"
              sx={{ width: 100, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
            />
          )}
          {orderType === 'STOP_LIMIT' && (
            <TextField
              size="small"
              type="number"
              label="Stop Limit Price"
              value={stopLimitPrice}
              onChange={(e) => setStopLimitPrice(e.target.value)}
              disabled={loading}
              placeholder="e.g. 679.50"
              sx={{ width: 100, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
            />
          )}
          {orderType === 'TRAILING_STOP' && (
            <>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <InputLabel>Trail Type</InputLabel>
                <Select
                  value={trailingStopType}
                  label="Trail Type"
                  onChange={(e) => setTrailingStopType(e.target.value)}
                  disabled={loading}
                  sx={{ fontSize: '0.75rem', height: 32 }}
                >
                  <MenuItem value="MARKET">Market</MenuItem>
                  <MenuItem value="LIMIT">Limit</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Trail Amount"
                value={trailingStopAmount}
                onChange={(e) => setTrailingStopAmount(e.target.value)}
                disabled={loading}
                placeholder="e.g. 1.00"
                sx={{ width: 80, '& input': { fontSize: '0.75rem', padding: '4px 8px' } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={trailingStopPercent}
                    onChange={(e) => setTrailingStopPercent(e.target.checked)}
                    size="small"
                    disabled={loading}
                  />
                }
                label="Percent"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              />
            </>
          )}
          {quotesLoading && <CircularProgress size={14} />}
        </Box>
        {/* Live payoff diagram — updates as legs/price change */}
        {payoffChart}

        {isOpeningOrder && (
          <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={otoEnabled}
                    onChange={(e) => setOtoEnabled(e.target.checked)}
                    size="small"
                    disabled={loading}
                  />
                }
                label="One Triggers Other — auto-place exit orders when entry fills"
              />
            </Box>
            {otoEnabled && (
              <>
                <Grid container spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Entry Price"
                      value={otoEntryPrice}
                      onChange={(e) => handleEntryPriceChange(e.target.value)}
                      disabled={loading}
                      placeholder="e.g. 1.00"
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth size="small"
                      type="number"
                      label="Total Qty"
                      value={quantity}
                      onChange={(e) => handleQtyChange(e.target.value)}
                      disabled={loading}
                      sx={{ '& input': { fontSize: '0.85rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Distributed across {Math.min(4, parseInt(quantity) || 0)} exit(s)
                    </Typography>
                  </Grid>
                </Grid>

                {/* Exit Orders */}
                <Box sx={{ mt: 1.5, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                    Exit Orders (triggered when entry fills)
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    {otoChildLegs.map((leg, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        <Typography variant="caption" fontWeight="bold">Leg {idx + 1}</Typography>
                        <Chip label={leg.instruction.replace(/_/g, ' ')} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {leg.putCall === 'C' ? 'Call' : 'Put'} @ {leg.strike} · {leg.expiration}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {exitOrders.map((exit, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Switch
                        checked={exit.enabled}
                        onChange={(e) => setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, enabled: e.target.checked } : eo))}
                        size="small"
                        disabled={loading || parseInt(exit.qty) === 0}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={`Gain %`}
                        value={exit.pct}
                        onChange={(e) => handleExitPctChange(idx, parseInt(e.target.value) || 0)}
                        disabled={loading || !exit.enabled}
                        sx={{ width: 80, '& input': { fontSize: '0.85rem' } }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Qty"
                        value={exit.qty}
                        onChange={(e) => setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, qty: e.target.value } : eo))}
                        disabled={loading || !exit.enabled}
                        sx={{ width: 70, '& input': { fontSize: '0.85rem' } }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Limit Price"
                        value={exit.price}
                        onChange={(e) => setExitOrders(prev => prev.map((eo, i) => i === idx ? { ...eo, price: e.target.value } : eo))}
                        disabled={loading || !exit.enabled}
                        sx={{ width: 100, '& input': { fontSize: '0.85rem' } }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        LIMIT
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* OCO Stop Order */}
                <Box sx={{ mt: 1, p: 1, border: 1, borderColor: 'warning.main', borderRadius: 1, backgroundColor: 'warning.light' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      OCO Stop Order (cancels limit exits when triggered)
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ocoStopEnabled}
                          onChange={(e) => setOcoStopEnabled(e.target.checked)}
                          size="small"
                          disabled={loading}
                        />
                      }
                      label="Enable"
                    />
                  </Box>
                  {ocoStopEnabled && (
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={6} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Stop Order Type</InputLabel>
                          <Select
                            value={ocoStopOrderType}
                            label="Stop Order Type"
                            onChange={(e) => setOcoStopOrderType(e.target.value)}
                            disabled={loading}
                            sx={{ fontSize: '0.85rem' }}
                          >
                            <MenuItem value="STOP">Stop</MenuItem>
                            <MenuItem value="STOP_LIMIT">Stop Limit</MenuItem>
                            <MenuItem value="TRAILING_STOP">Trailing Stop</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {(ocoStopOrderType === 'STOP' || ocoStopOrderType === 'STOP_LIMIT') && (
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth size="small"
                            type="number"
                            label="Stop Price"
                            value={ocoStopPrice}
                            onChange={(e) => setOcoStopPrice(e.target.value)}
                            disabled={loading}
                            placeholder="e.g. 680.00"
                            sx={{ '& input': { fontSize: '0.85rem' } }}
                          />
                        </Grid>
                      )}
                      {ocoStopOrderType === 'STOP_LIMIT' && (
                        <Grid item xs={6} sm={4}>
                          <TextField
                            fullWidth size="small"
                            type="number"
                            label="Stop Limit Price"
                            value={ocoStopLimitPrice}
                            onChange={(e) => setOcoStopLimitPrice(e.target.value)}
                            disabled={loading}
                            placeholder="e.g. 679.50"
                            sx={{ '& input': { fontSize: '0.85rem' } }}
                          />
                        </Grid>
                      )}
                      {ocoStopOrderType === 'TRAILING_STOP' && (
                        <>
                          <Grid item xs={6} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Trail Type</InputLabel>
                              <Select
                                value={ocoTrailingStopType}
                                label="Trail Type"
                                onChange={(e) => setOcoTrailingStopType(e.target.value)}
                                disabled={loading}
                                sx={{ fontSize: '0.85rem' }}
                              >
                                <MenuItem value="MARKET">Market</MenuItem>
                                <MenuItem value="LIMIT">Limit</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth size="small"
                              type="number"
                              label="Trail Amount"
                              value={ocoTrailingStopAmount}
                              onChange={(e) => setOcoTrailingStopAmount(e.target.value)}
                              disabled={loading}
                              placeholder="e.g. 1.00"
                              sx={{ '& input': { fontSize: '0.85rem' } }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={ocoTrailingStopPercent}
                                  onChange={(e) => setOcoTrailingStopPercent(e.target.checked)}
                                  size="small"
                                  disabled={loading}
                                />
                              }
                              label="Percent"
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} size="small">Close</Button>
        <Button
          onClick={handlePlaceOrder}
          variant="contained"
          color="primary"
          disabled={loading || !selectedAccountId || orderLegs.length === 0}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          size="small"
        >
          {loading ? 'Placing...' : result?.success ? 'Place Another Order' : 'Place Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
